import { Tooltip } from '@mui/material'
import { getRoute } from 'helpers/route.helper/route.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { PropsWithChildren, ReactElement, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import filterStore from 'store/filter/filter'
import Icon from 'ui/atoms/Icon/Icon'
import { ToggleButton } from 'ui/molecules/ToggleButton/ToggleButton'
import { useSnapshot } from 'valtio'
import FilterComponent from '../Filter/FilterComponent'
import InfoBox from './InfoBox'
import styles from './ModuleHeader.module.less'

type ModuleHeaderProps = {
  /** Leave it out when the children lay out the title themselves. */
  infoHeader?: string | React.JSX.Element
  onInfoButtonClick?: () => void
  beta?: boolean
  secondaryChildren?: ReactNode
  includePropertyFilter?: boolean
}

export default function ModuleHeader({
  infoHeader,
  onInfoButtonClick,
  children,
  beta,
  secondaryChildren,
  includePropertyFilter = true,
}: PropsWithChildren<ModuleHeaderProps>): ReactElement {
  const { t } = useTranslation()

  const { filterComponentPerRoute, tagsPerUiconfig } = useSnapshot(filterStore)
  const [filterIsCollapsed, setFilterCollapsed] = useState<boolean>(true)
  const route = getRoute()

  const { activeTags, isActiveTagsFiltered } = useActiveTags({
    availableFiltersSubset: tagsPerUiconfig,
  })

  const routeHasFilterComponent = filterComponentPerRoute[route]

  const hasFilterData = activeTags.size > 0

  const toggleFilterCollapse = (): void => {
    setFilterCollapsed(!filterIsCollapsed)
  }

  const filterIcon = useMemo(
    () =>
      isActiveTagsFiltered ? (
        <Icon tooltip={{ title: t('Filters active'), placement: 'right' }} icon="fas fa-filter" />
      ) : (
        ''
      ),
    [t, isActiveTagsFiltered]
  )

  return (
    <div className={styles.ModuleHeader_Root}>
      <div className={styles.ModuleHeader_RootRow}>
        {infoHeader && (
          <div className={styles.ModuleHeader_InfoBoxContainer}>
            <InfoBox infoHeader={infoHeader} onInfoButtonClick={onInfoButtonClick} beta={beta} />
          </div>
        )}
        <div className={styles.ModuleHeader_MiddleRow}>
          {routeHasFilterComponent && (
            <Tooltip
              title={
                !hasFilterData
                  ? t('No data to filter. Try reloading the page or open up a card if all are collapsed')
                  : ''
              }
              placement="bottom"
              arrow
            >
              <div className={styles.ModuleHeader_FilterButtonContainer}>
                <ToggleButton isCollapsed={filterIsCollapsed} onClick={toggleFilterCollapse} disabled={!hasFilterData}>
                  <span>
                    {t('Filter')}
                    {filterIcon}
                  </span>
                </ToggleButton>
              </div>
            </Tooltip>
          )}
          {children}
        </div>
      </div>
      {includePropertyFilter && (
        <div style={{ display: filterIsCollapsed ? 'none' : 'block' }}>
          <FilterComponent availableFiltersSubset={tagsPerUiconfig} activeTags={activeTags} />
        </div>
      )}
      <div className={styles.ModuleHeader_SecondaryChildrenContainer}>{secondaryChildren}</div>
    </div>
  )
}
